import fileImport from './fileImport';
import defaultRule from './willChange_import_import';
import { constants } from 'lib_ui-services';
import {
    getPhysicalInventoryIdFromFileName,
    PALM_AMI004_FILE_NAME_CONVENTION,
    PALM_AMI004_FILE_NAME_EXAMPLE
} from '../../../../utilities/palmAmi004FileName';
const { useCaseIds, notificationTypes } = constants;

const _p = { loadFile: fileImport.getData };
export const _private = _p;

export default {
    verb: 'willChange',
    namespace: 'import',
    relation: 'import',
    // this needs to happen BEFORE the default rule
    priority: defaultRule.priority + 10,
    useCaseIds: [useCaseIds.PALM],
    description:
        'Map alternative titles onto the dataModel for a better AMI004 match, and name the import after the file',
    logic: willChange
};

/**
 *
 * @param {object} message
 * @param {Array<Array<string|number|bool|Date>>} message.newRecord
 * @returns
 */
async function willChange({ data, log, dispatch }) {
    let { propertyName, newValue, newRecord } = data;
    if (propertyName !== 'file' || !newValue?.length) return data;
    const file = newValue[0];
    const dataSample = (await _p.loadFile(file)).slice(0, 1);
    if (isAMI004(dataSample)) {
        newRecord.dataModel = mapAMI004DataModel(newRecord.dataModel);
        // The import title is also the inventory name: the server assures an inventory:inventory
        // record carrying the import's _id and title (see willImport_item_item_flair in the
        // rules-engine silo, `import name = inventory name`), and the AMI008 export names its
        // file after that inventory. So the PHYSICAL_INVENTORY_ID PALM sends us in the AMI004
        // file name has to land here to survive the round trip.
        const physicalInventoryId = getPhysicalInventoryIdFromFileName(file.name);
        if (physicalInventoryId) {
            newRecord.title = physicalInventoryId;
        } else {
            // A hand-renamed file is not a reason to block an otherwise valid inventory; keep the
            // generated default title instead. Tell the user at file-selection time though, while
            // they can still pick a different file: the consequence only shows up much later, when
            // the AMI008 export is named after that generated title and PALM cannot correlate it.
            log?.warn(
                `Import file '${file.name}' does not match the PALM AMI004 naming convention ` +
                    `(${PALM_AMI004_FILE_NAME_CONVENTION}). Keeping the default import title.`
            );
            await dispatch?.(
                {
                    addToList: false,
                    // A concrete example, not the `${placeholder}` pattern - this is read by the
                    // person who just picked the file, who needs to compare it against their own.
                    message:
                        `'${file.name}' is not named the way PALM names AMI004 files ` +
                        `(for example ${PALM_AMI004_FILE_NAME_EXAMPLE}), so this inventory will not be ` +
                        'named after its physical inventory ID.',
                    variant: 'warning',
                    type: notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        }
    }
    return { ...data, newRecord };
}

const AMI004Headers = [
    'TAG_NUMBER',
    'SERIAL_ID',
    'ASSET_ID',
    'DEPTID',
    'MODEL',
    'LOCATION',
    'AREA ID',
    'DESCR',
    'CUSTODIAN',
    'EMPLID',
    'CUSTODIAN_DEPTID',
    'MANUFACTURER',
    'MATCH_STATUS',
    'REVIEW_SW',
    'IP_ADDRESS',
    'IP_ALIAS',
    'SOFTWARE_VERSION',
    'IT_ASSET_ID',
    'SCAN_BUSINESS_UNIT',
    'HAND_HELD_OPERATOR',
    'COMMENTS'
];
function isAMI004(dataSample) {
    const headers = dataSample[0].map(c => c.toUpperCase());
    // it is AMI004 if (at least) all known AMI004 headers are included
    return AMI004Headers.every(h => headers.includes(h));
}

// Keyed three ways, most specific first (see aliasForField):
//   `${foreignNamespace}:${foreignRelation}.${propertyName}` for a relation that carries more
//   than one AMI004 column and needs the inner property to tell them apart;
//   `${foreignNamespace}:${foreignRelation}` for a lookup whose inner property is the tenant's
//   to choose (PALM keys location:location on `location` where REX keys it on `title`, so a
//   full-path key would miss - AB#9654);
//   the plain property path for everything that is not a lookup.
const AMI004_ALIASES = {
    assetNo: ['TAG_NUMBER'],
    serialNo: ['SERIAL_ID'],
    palmAssetId: ['ASSET_ID'],
    'location:location': ['LOCATION'],
    'organization:organization': ['DEPTID'],
    'item:model': ['MODEL'],
    'item:manufacturer': ['MANUFACTURER'],
    description: ['DESCR'],
    // organization:person carries three of the AMI004 columns. EMPLID and CUSTODIAN_DEPTID are
    // fixed properties of a person, so they are keyed by full path; CUSTODIAN is displayed by
    // whichever property the tenant configured, so it keeps the relation-only key and matches
    // any custodian column that is not one of the other two.
    'organization:person': ['CUSTODIAN'],
    'organization:person.emplId': ['EMPLID'],
    'organization:person.department': ['CUSTODIAN_DEPTID'],
    notes: ['COMMENTS'],
    'location:subLocation': ['AREA ID'],
    ipAddress: ['IP_ADDRESS'],
    ipAlias: ['IP_ALIAS'],
    itAssetId: ['IT_ASSET_ID'],
    softwareVersion: ['SOFTWARE_VERSION'],
    scanBusinessUnit: ['SCAN_BUSINESS_UNIT'],
    handHeldOperator: ['HAND_HELD_OPERATOR'],
    matchStatus: ['MATCH_STATUS'],
    reviewSW: ['REVIEW_SW']
};

const AMI004_PASSTHROUGH_FIELDS = [
    {
        _meta: {
            id: 'areaId',
            // hNodeType: 'ShortText',
            hNodeType: 'DropDownWithFilter',
            hNodeTypeGroup: 'formElement',
            title: 'Area ID',
            propertyName: 'title',
            foreignNamespace: 'location',
            foreignRelation: 'subLocation',
            dataType: { type: 'Lookup' },
            alias: ['AREA ID']
        }
    },
    // These three write into a single object keyed `organization:person`, using each column's
    // propertyName as the inner key (transformRowToObject in willCreate_import_import). So all
    // three values end up on one person - which is the intent - but only while `emplId`,
    // `department` and the custodian's own propertyName stay distinct. Give two of them the same
    // propertyName and one value silently replaces the other.
    //
    // A tenant that exposes the custodian on the item form supplies its own entry for it, and
    // that one wins (the append below skips any AMI004 header the form already covers). This
    // entry is for the tenants that do not, where the CUSTODIAN column would otherwise have
    // nothing to map to. `title` is the property to default to: organization:person has no
    // titleAlternative, and it is the property ami008Columns exports CUSTODIAN from.
    {
        _meta: {
            id: 'custodian',
            hNodeType: 'DropDownWithFilter',
            hNodeTypeGroup: 'formElement',
            title: 'Custodian',
            propertyName: 'title',
            foreignNamespace: 'organization',
            foreignRelation: 'person',
            dataType: { type: 'Lookup' },
            alias: ['CUSTODIAN']
        }
    },
    {
        _meta: {
            id: 'custodianEmployeeID',
            hNodeType: 'DropDownWithFilter',
            hNodeTypeGroup: 'formElement',
            title: 'Custodian Employee ID',
            propertyName: 'emplId',
            foreignNamespace: 'organization',
            foreignRelation: 'person',
            dataType: { type: 'Lookup' },
            alias: ['EMPLID']
        }
    },
    {
        _meta: {
            id: 'custodianDepartmentID',
            hNodeType: 'DropDownWithFilter',
            hNodeTypeGroup: 'formElement',
            title: 'Custodian Dept ID',
            propertyName: 'department',
            foreignNamespace: 'organization',
            foreignRelation: 'person',
            dataType: { type: 'Lookup' },
            alias: ['CUSTODIAN_DEPTID']
        }
    },
    {
        _meta: {
            id: 'ipAddress',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'IP Address',
            propertyName: 'ipAddress',
            alias: ['IP_ADDRESS']
        }
    },
    {
        _meta: {
            id: 'ipAlias',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'IP Alias',
            propertyName: 'ipAlias',
            alias: ['IP_ALIAS']
        }
    },
    {
        _meta: {
            id: 'itAssetId',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'IT Asset ID',
            propertyName: 'itAssetId',
            alias: ['IT_ASSET_ID']
        }
    },
    // and the fields we don't care about.
    {
        _meta: {
            id: 'softwareVersion',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'Software Version',
            propertyName: 'softwareVersion',
            alias: ['SOFTWARE_VERSION'],
            excludeFromImport: true
        }
    },
    {
        _meta: {
            id: 'scanBusinessUnit',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'Scan Business Unit',
            propertyName: 'scanBusinessUnit',
            alias: ['SCAN_BUSINESS_UNIT'],
            excludeFromImport: true
        }
    },
    {
        _meta: {
            id: 'handHeldOperator',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'Hand Held Operator',
            propertyName: 'handHeldOperator',
            alias: ['HAND_HELD_OPERATOR'],
            excludeFromImport: true
        }
    },
    {
        _meta: {
            id: 'matchStatus',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'Match Status',
            propertyName: 'matchStatus',
            alias: ['MATCH_STATUS'],
            excludeFromImport: true
        }
    },
    {
        _meta: {
            id: 'reviewSW',
            hNodeType: 'ShortText',
            hNodeTypeGroup: 'formElement',
            title: 'Review SW',
            propertyName: 'reviewSW',
            alias: ['REVIEW_SW'],
            excludeFromImport: true
        }
    }
];

function mapAMI004DataModel(dataModel) {
    // append data models with alias information
    let result = dataModel.map(model => {
        model._meta.alias = aliasForField(model);
        return model;
    });

    // add missing fields, not correctly extracted by our current data Dictionary extraction,
    // and fields we don't care about, but need to import just for the sake of exporting them
    // if they are not included yet.
    AMI004_PASSTHROUGH_FIELDS.forEach(field => {
        // A passthrough exists only to supply an AMI004 column the tenant's form does not, so
        // skip it once either the property it writes to or the column it maps to is accounted
        // for. Matching on the header as well as the path matters where the two disagree: a
        // tenant that displayed the custodian by some property other than `title` would cover
        // CUSTODIAN at a path this entry's `title` default does not recognize, and appending both
        // would leave two dataModel entries competing for the one column.
        const covered = result.some(
            model =>
                serializePath(model) === serializePath(field) ||
                sameAMI004Column(model._meta.alias, field._meta.alias)
        );
        if (!covered) {
            result.push(field);
        }
    });
    return result;
}

/**
 * Find the AMI004 header(s) a dataModel entry should auto-map to, from most specific key to
 * least. See the note on AMI004_ALIASES for why lookups are keyed two ways.
 *
 * Entries that land on the relation-only key can still collide: createDataModel.fromHNodes
 * produces one entry per displayed property of a lookup (the `inputDisplayProperties` special
 * case - one column shows `title`, another `description` of the same location:location), and
 * both get the same alias here. Downstream, guessColumns takes the first and removes it from
 * the pool, so one column auto-maps and the other views are mapped by hand in the UI.
 *
 * @param {{ _meta: {foreignNamespace?: string; foreignRelation?: string; propertyName: string; propertyPath?: string;}}} field
 * @returns {Array<string>|undefined}
 */
function aliasForField(field) {
    const { propertyName, foreignNamespace, foreignRelation, propertyPath } = field._meta;
    if (!foreignNamespace) {
        return AMI004_ALIASES[(propertyPath ? propertyPath + '.' : '') + propertyName];
    }
    return AMI004_ALIASES[serializePath(field)] ?? AMI004_ALIASES[`${foreignNamespace}:${foreignRelation}`];
}

/**
 * Every alias in AMI004_ALIASES names exactly one AMI004 column, so two entries collide when
 * they resolve to the same header.
 *
 * @param {Array<string>} [alias]
 * @param {Array<string>} [otherAlias]
 */
function sameAMI004Column(alias, otherAlias) {
    return Boolean(alias?.length) && alias[0] === otherAlias?.[0];
}

/**
 * @param {{ _meta: {foreignNamespace?: string; foreignRelation?: string; propertyName: string;}}} field
 */
function serializePath(field) {
    return `${field._meta.foreignNamespace || ''}:${field._meta.foreignRelation || ''}.${field._meta.propertyName}`;
}
